/**
 * Sample analytics for the signed-in Analytics screen.
 * Replace `snapshotFor` when the reporting API is ready.
 * The 7-day card figures follow the product design.
 */

export type Tone = 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'cyan' | 'slate';

export type AnalyticsPeriod = 'today' | '7d' | '30d' | 'custom';

export interface DateRange {
  start: string;
  end: string;
}

export interface KpiSnapshot {
  networkMb: number;
  networkDelta: number;
  networkSpark: number[];
  apiCalls: number;
  apiDelta: number;
  apiSpark: number[];
  mockingMinutes: number;
  mockingDelta: number;
  timeSpark: number[];
  completedSyncs: number;
  syncSuccessPct: number;
  syncSpark: number[];
}

export interface UsagePoint {
  label: string;
  shortLabel: string;
  mobileKb: number;
  wifiKb: number;
}

export interface TrafficSlice {
  id: string;
  label: string;
  tone: Tone;
  calls: number;
  dataMb: number;
}

export interface ModuleStat {
  id: string;
  label: string;
  tone: Tone;
  minutes: number;
  timeShare: number;
  sessions: number;
}

export interface SyncBar {
  label: string;
  shortLabel: string;
  manual: number;
  auto: number;
  login: number;
  other: number;
}

export interface LatestSync {
  id: string;
  title: string;
  when: string;
  duration: string;
  status: 'Completed' | 'Failed';
  device: string;
  detail: string;
}

export interface AnalyticsSnapshot {
  empty: boolean;
  start: string;
  end: string;
  rangeLabel: string;
  kpis: KpiSnapshot;
  usage: UsagePoint[];
  /** Y-axis ceiling in KB. Large values render as MB. */
  usageAxisKb: number;
  traffic: TrafficSlice[];
  modules: ModuleStat[];
  syncs: SyncBar[];
  syncAxis: number;
  latest: LatestSync | null;
  resources: {
    batterySessions: number;
    localEvents: number;
    mobileMb: number;
    wifiMb: number;
  };
  insights: {
    peakLabel: string;
    peakText: string;
    moduleLabel: string;
    moduleText: string;
    mobileMb: number;
    mobileText: string;
  };
}

export const SAMPLE_START = '2026-09-25';
export const SAMPLE_END = '2026-10-24';
export const WEEK_START = '2026-10-18';
export const WEEK_END = '2026-10-24';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const TRAFFIC_META: Array<{ id: string; label: string; tone: Tone }> = [
  { id: 'main', label: 'Main API', tone: 'blue' },
  { id: 'google', label: 'Google API', tone: 'orange' },
  { id: 'admin', label: 'Admin push', tone: 'purple' },
  { id: 'image', label: 'Image upload', tone: 'green' },
  { id: 'other', label: 'Other API', tone: 'slate' },
];

const MODULE_META: Array<{ id: string; label: string; tone: Tone }> = [
  { id: 'stationary', label: 'Stationary Pointer', tone: 'purple' },
  { id: 'playlist', label: 'Playlist', tone: 'orange' },
  { id: 'itinerary', label: 'Auto Itinerary', tone: 'blue' },
  { id: 'joystick', label: 'Joystick', tone: 'green' },
  { id: 'polygon', label: 'Polygon Mover', tone: 'red' },
  { id: 'bluetooth', label: 'Bluetooth Controller', tone: 'cyan' },
  { id: 'recorder', label: 'Location Recorder', tone: 'slate' },
];

export function parseISODate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function toISODate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function formatPrettyDate(iso: string): string {
  const date = parseISODate(iso);
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatRange(start: string, end: string): string {
  if (start === end) return formatPrettyDate(start);
  const from = parseISODate(start);
  const to = parseISODate(end);
  if (from.getFullYear() === to.getFullYear()) {
    return `${from.getDate()} ${MONTHS[from.getMonth()]} – ${to.getDate()} ${MONTHS[to.getMonth()]}, ${to.getFullYear()}`;
  }
  return `${formatPrettyDate(start)} – ${formatPrettyDate(end)}`;
}

export function formatMb(value: number): string {
  return `${value.toFixed(2)} MB`;
}

export function formatInt(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

export function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function formatDuration(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  if (hours <= 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function dayLabel(iso: string): { label: string; shortLabel: string } {
  const date = parseISODate(iso);
  return {
    label: `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}`,
    shortLabel: String(date.getDate()),
  };
}

function dayPoint(iso: string, mobileKb: number, wifiKb: number): UsagePoint {
  return { ...dayLabel(iso), mobileKb, wifiKb };
}

function hourPoint(label: string, shortLabel: string, mobileKb: number, wifiKb: number): UsagePoint {
  return { label, shortLabel, mobileKb, wifiKb };
}

function syncWhen(end: string, duration: string, clock = '22:23'): string {
  return `Completed ${formatPrettyDate(end)}, ${clock} · Duration ${duration}`;
}

function traffic(calls: number[], dataMb: number[]): TrafficSlice[] {
  return TRAFFIC_META.map((item, index) => ({
    ...item,
    calls: calls[index] || 0,
    dataMb: dataMb[index] || 0,
  }));
}

function modules(minutes: number[], shares: number[], sessions: number[]): ModuleStat[] {
  return MODULE_META.map((item, index) => ({
    ...item,
    minutes: minutes[index] || 0,
    timeShare: shares[index] || 0,
    sessions: sessions[index] || 0,
  }));
}

function latest(
  end: string,
  title: string,
  duration: string,
  device: string,
  detail: string,
  clock = '22:23'
): LatestSync {
  return {
    id: title.toLowerCase().replace(/\s+/g, '-'),
    title,
    when: syncWhen(end, duration, clock),
    duration,
    status: 'Completed',
    device,
    detail,
  };
}

const WEEK: AnalyticsSnapshot = {
  empty: false,
  start: WEEK_START,
  end: WEEK_END,
  rangeLabel: formatRange(WEEK_START, WEEK_END),
  kpis: {
    networkMb: 8.42,
    networkDelta: 12.4,
    networkSpark: [12, 14, 13, 18, 22, 21, 28, 34],
    apiCalls: 1284,
    apiDelta: 8.2,
    apiSpark: [80, 96, 88, 120, 140, 132, 168, 190],
    mockingMinutes: 766,
    mockingDelta: 18.6,
    timeSpark: [20, 28, 24, 40, 52, 48, 64, 78],
    completedSyncs: 42,
    syncSuccessPct: 95.2,
    syncSpark: [3, 4, 4, 6, 8, 7, 9, 11],
  },
  usageAxisKb: 200,
  usage: [
    dayPoint('2026-10-18', 14, 22),
    dayPoint('2026-10-19', 20, 12),
    dayPoint('2026-10-20', 24, 72),
    dayPoint('2026-10-21', 54, 132),
    dayPoint('2026-10-22', 18, 108),
    dayPoint('2026-10-23', 14, 82),
    dayPoint('2026-10-24', 46, 156),
  ],
  traffic: traffic([642, 256, 168, 142, 76], [4.21, 1.68, 1.1, 0.94, 0.49]),
  modules: modules(
    [72, 268, 156, 312, 48, 24, 12],
    [7.9, 30.6, 17.8, 35.5, 5.4, 2.7, 1.4],
    [8, 27, 16, 37, 5, 3, 2]
  ),
  syncAxis: 20,
  syncs: [
    { ...dayLabel('2026-10-18'), manual: 2, auto: 2, login: 2, other: 1 },
    { ...dayLabel('2026-10-19'), manual: 2, auto: 3, login: 2, other: 1 },
    { ...dayLabel('2026-10-20'), manual: 3, auto: 2, login: 2, other: 1 },
    { ...dayLabel('2026-10-21'), manual: 4, auto: 5, login: 4, other: 2 },
    { ...dayLabel('2026-10-22'), manual: 2, auto: 3, login: 2, other: 1 },
    { ...dayLabel('2026-10-23'), manual: 5, auto: 6, login: 4, other: 3 },
    { ...dayLabel('2026-10-24'), manual: 3, auto: 3, login: 2, other: 2 },
  ],
  latest: latest(WEEK_END, 'Login sync', '4s', 'Samsung F42', 'Account data synced after sign-in.'),
  resources: {
    batterySessions: 6,
    localEvents: 326,
    mobileMb: 0,
    wifiMb: 8.42,
  },
  insights: {
    peakLabel: '10 PM',
    peakText: 'Highest API usage was at 10 PM.',
    moduleLabel: 'Joystick',
    moduleText: '35.5% of total mocking time.',
    mobileMb: 0,
    mobileText: 'No mobile data used in this period.',
  },
};

const TODAY: AnalyticsSnapshot = {
  empty: false,
  start: SAMPLE_END,
  end: SAMPLE_END,
  rangeLabel: formatRange(SAMPLE_END, SAMPLE_END),
  kpis: {
    networkMb: 0.86,
    networkDelta: 4.1,
    networkSpark: [4, 6, 5, 8, 7, 11, 10, 14],
    apiCalls: 146,
    apiDelta: -3.2,
    apiSpark: [22, 18, 20, 16, 19, 14, 15, 12],
    mockingMinutes: 66,
    mockingDelta: 2.4,
    timeSpark: [4, 6, 5, 8, 7, 10, 9, 12],
    completedSyncs: 6,
    syncSuccessPct: 100,
    syncSpark: [1, 1, 2, 1, 2, 1, 2, 2],
  },
  usageAxisKb: 40,
  usage: [
    hourPoint('8 AM', '8', 2, 6),
    hourPoint('11 AM', '11', 4, 10),
    hourPoint('2 PM', '2', 3, 14),
    hourPoint('5 PM', '5', 6, 18),
    hourPoint('8 PM', '8', 5, 22),
    hourPoint('10 PM', '10', 8, 28),
  ],
  traffic: traffic([74, 28, 18, 16, 10], [0.36, 0.18, 0.12, 0.11, 0.09]),
  modules: modules(
    [6, 12, 8, 27, 6, 4, 3],
    [9.1, 18.2, 12.1, 41, 9.1, 6.1, 4.4],
    [1, 2, 1, 4, 1, 1, 1]
  ),
  syncAxis: 4,
  syncs: [
    { label: '8 AM', shortLabel: '8', manual: 1, auto: 0, login: 0, other: 0 },
    { label: '11 AM', shortLabel: '11', manual: 0, auto: 1, login: 0, other: 0 },
    { label: '2 PM', shortLabel: '2', manual: 1, auto: 0, login: 0, other: 0 },
    { label: '5 PM', shortLabel: '5', manual: 0, auto: 0, login: 1, other: 0 },
    { label: '8 PM', shortLabel: '8', manual: 1, auto: 0, login: 0, other: 0 },
    { label: '10 PM', shortLabel: '10', manual: 0, auto: 0, login: 1, other: 0 },
  ],
  latest: latest(SAMPLE_END, 'Login sync', '4s', 'Samsung F42', 'Account data synced after sign-in.'),
  resources: {
    batterySessions: 1,
    localEvents: 48,
    mobileMb: 0.12,
    wifiMb: 0.74,
  },
  insights: {
    peakLabel: '9 PM',
    peakText: 'Highest API usage was at 9 PM.',
    moduleLabel: 'Joystick',
    moduleText: '41.0% of total mocking time.',
    mobileMb: 0.12,
    mobileText: 'A little mobile data was used today.',
  },
};

const MONTH_START = '2026-09-25';

const MONTH: AnalyticsSnapshot = {
  empty: false,
  start: MONTH_START,
  end: SAMPLE_END,
  rangeLabel: formatRange(MONTH_START, SAMPLE_END),
  kpis: {
    networkMb: 31.6,
    networkDelta: 9.1,
    networkSpark: [18, 22, 20, 28, 26, 34, 32, 40],
    apiCalls: 4820,
    apiDelta: 11.4,
    apiSpark: [320, 380, 360, 440, 410, 520, 490, 560],
    mockingMinutes: 2772,
    mockingDelta: 6.8,
    timeSpark: [30, 36, 34, 48, 44, 58, 52, 64],
    completedSyncs: 168,
    syncSuccessPct: 93.4,
    syncSpark: [12, 16, 14, 22, 18, 26, 24, 28],
  },
  usageAxisKb: 8192,
  usage: [
    dayPoint('2026-09-25', 200, 1800),
    dayPoint('2026-09-30', 280, 2400),
    dayPoint('2026-10-04', 240, 2100),
    dayPoint('2026-10-09', 520, 4200),
    dayPoint('2026-10-14', 300, 3600),
    dayPoint('2026-10-19', 640, 5100),
    dayPoint('2026-10-22', 400, 4800),
    dayPoint('2026-10-24', 700, 6200),
  ],
  traffic: traffic([2410, 960, 628, 534, 288], [15.8, 6.3, 4.12, 3.52, 1.86]),
  modules: modules(
    [349, 787, 504, 665, 225, 144, 98],
    [12.6, 28.4, 18.2, 24, 8.1, 5.2, 3.5],
    [22, 64, 38, 58, 14, 9, 6]
  ),
  syncAxis: 40,
  syncs: [
    { ...dayLabel('2026-09-25'), manual: 6, auto: 8, login: 4, other: 2 },
    { ...dayLabel('2026-10-02'), manual: 8, auto: 10, login: 5, other: 3 },
    { ...dayLabel('2026-10-09'), manual: 7, auto: 9, login: 4, other: 2 },
    { ...dayLabel('2026-10-16'), manual: 10, auto: 12, login: 6, other: 4 },
    { ...dayLabel('2026-10-23'), manual: 9, auto: 11, login: 5, other: 3 },
    { ...dayLabel('2026-10-24'), manual: 8, auto: 10, login: 6, other: 2 },
  ],
  latest: latest(SAMPLE_END, 'Auto sync', '11s', 'Pixel 8', 'Scheduled sync finished on Wi-Fi.', '18:02'),
  resources: {
    batterySessions: 18,
    localEvents: 1240,
    mobileMb: 1.4,
    wifiMb: 30.2,
  },
  insights: {
    peakLabel: '8 PM',
    peakText: 'Highest API usage was at 8 PM.',
    moduleLabel: 'Playlist',
    moduleText: '28.4% of total mocking time.',
    mobileMb: 1.4,
    mobileText: 'Mobile data stayed light across the month.',
  },
};

function isISODate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function inclusiveDays(start: string, end: string): number {
  const ms = parseISODate(end).getTime() - parseISODate(start).getTime();
  return Math.round(ms / 86400000) + 1;
}

function sampleDays(start: string, end: string, count: number): string[] {
  if (count <= 0) return [];
  if (count === 1) return [start];
  const from = parseISODate(start).getTime();
  const to = parseISODate(end).getTime();
  const days: string[] = [];
  for (let index = 0; index < count; index += 1) {
    days.push(toISODate(new Date(from + ((to - from) * index) / (count - 1))));
  }
  return days;
}

function emptySnapshot(start: string, end: string): AnalyticsSnapshot {
  const from = isISODate(start) ? start : WEEK_START;
  const to = isISODate(end) ? end : from;
  const safeFrom = from <= to ? from : to;
  const safeTo = from <= to ? to : from;
  return {
    empty: true,
    start: safeFrom,
    end: safeTo,
    rangeLabel: formatRange(safeFrom, safeTo),
    kpis: {
      networkMb: 0,
      networkDelta: 0,
      networkSpark: [0, 0, 0, 0],
      apiCalls: 0,
      apiDelta: 0,
      apiSpark: [0, 0, 0, 0],
      mockingMinutes: 0,
      mockingDelta: 0,
      timeSpark: [0, 0, 0, 0],
      completedSyncs: 0,
      syncSuccessPct: 0,
      syncSpark: [0, 0, 0, 0],
    },
    usage: [],
    usageAxisKb: 0,
    traffic: [],
    modules: [],
    syncs: [],
    syncAxis: 0,
    latest: null,
    resources: { batterySessions: 0, localEvents: 0, mobileMb: 0, wifiMb: 0 },
    insights: {
      peakLabel: '—',
      peakText: 'No API activity in this range.',
      moduleLabel: '—',
      moduleText: 'No module ran in this range.',
      mobileMb: 0,
      mobileText: 'No mobile data used in this period.',
    },
  };
}

function stamp(base: AnalyticsSnapshot, start: string, end: string): AnalyticsSnapshot {
  const usageDays = sampleDays(start, end, base.usage.length);
  const syncDays = sampleDays(start, end, base.syncs.length);
  const hourly = base.usage.some((point) => point.label.includes('AM') || point.label.includes('PM'));
  return {
    ...base,
    empty: false,
    start,
    end,
    rangeLabel: formatRange(start, end),
    usage: hourly
      ? base.usage
      : base.usage.map((point, index) => ({ ...point, ...dayLabel(usageDays[index] || start) })),
    syncs: hourly
      ? base.syncs
      : base.syncs.map((point, index) => ({ ...point, ...dayLabel(syncDays[index] || start) })),
    latest: base.latest
      ? { ...base.latest, when: syncWhen(end, base.latest.duration) }
      : null,
  };
}

export function customSnapshot(start: string, end: string): AnalyticsSnapshot {
  if (!isISODate(start) || !isISODate(end)) return emptySnapshot(start, end);
  const from = start <= end ? start : end;
  const to = start <= end ? end : start;
  if (to < SAMPLE_START || from > SAMPLE_END) return emptySnapshot(from, to);
  const days = inclusiveDays(from, to);
  if (days <= 1) return stamp(TODAY, from, to);
  if (days <= 14) return stamp(WEEK, from, to);
  return stamp(MONTH, from, to);
}

export function snapshotFor(period: AnalyticsPeriod, custom?: DateRange): AnalyticsSnapshot {
  if (period === 'today') return TODAY;
  if (period === '30d') return MONTH;
  if (period === 'custom' && custom) return customSnapshot(custom.start, custom.end);
  return WEEK;
}

function cell(value: string | number): string {
  const text = String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function analyticsCsv(snapshot: AnalyticsSnapshot): string {
  const rows: Array<Array<string | number>> = [
    ['range', snapshot.rangeLabel],
    ['activity', snapshot.empty ? 'none' : 'sample'],
  ];
  if (!snapshot.empty) {
    rows.push(
      ['network_mb', snapshot.kpis.networkMb],
      ['network_delta_pct', snapshot.kpis.networkDelta],
      ['api_calls', snapshot.kpis.apiCalls],
      ['api_delta_pct', snapshot.kpis.apiDelta],
      ['mocking_minutes', snapshot.kpis.mockingMinutes],
      ['mocking_delta_pct', snapshot.kpis.mockingDelta],
      ['completed_syncs', snapshot.kpis.completedSyncs],
      ['sync_success_pct', snapshot.kpis.syncSuccessPct],
      ['mobile_mb', snapshot.resources.mobileMb],
      ['wifi_mb', snapshot.resources.wifiMb],
      ['usage_day', 'mobile_kb', 'wifi_kb']
    );
    snapshot.usage.forEach((point) => rows.push([point.label, point.mobileKb, point.wifiKb]));
    rows.push(['category', 'calls', 'data_mb']);
    snapshot.traffic.forEach((slice) => rows.push([slice.label, slice.calls, slice.dataMb]));
    rows.push(['module', 'minutes', 'sessions']);
    snapshot.modules.forEach((row) => rows.push([row.label, row.minutes, row.sessions]));
  }
  return rows.map((row) => row.map(cell).join(',')).join('\n');
}
